import { memo, useEffect, useState } from 'react';

import NoteCard from '@/components/NoteCard';
import PageHeader from '@/components/PageHeader';
import Pagination from '@/components/Pagination';
import NoteFilterForm from '@/forms/NoteFilterForm';
import { NoteFilters } from '@/forms/NoteFilterForm/types';
import useAPI from '@/hooks/useAPI';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import usePagination from '@/hooks/usePagination';
import useQueryString from '@/hooks/useQueryString';
import NoteRepository from '@/repositories/NoteRepository';
import { Note, NoteParams } from '@/types/note';
import Button from '@/ui/components/Button';
import Container from '@/ui/components/Container';
import Heading from '@/ui/components/Heading/Heading';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';

interface Props {
  object_id: string;
  object_name: string;
  contentTypeName: string;
}

const NoteList = ({ object_id, object_name, contentTypeName }: Props) => {
  const [totalPages, setTotalPages] = useState<number>(0);
  const { navigateToNewNote } = useNavigateToPage();
  const {
    params: queryParams,
    setParams: setQueryParams,
    hasQueryParams
  } = useQueryString<Partial<NoteParams>>();
  const [hasInteractedWithPaginator, setHasInteractedWithPaginator] = useState(false);
  const [params, setParams] = useState<NoteParams>((): NoteParams => {
    // Initialize from query params
    if (hasQueryParams) {
      return {
        ...queryParams,
        page: queryParams.page || '1'
      } as NoteParams;
    }

    // Apply defaults
    return {
      note_type: undefined,
      page: '1'
    };
  });
  const noteRepository = NoteRepository();

  const setFilters = (filters: Partial<NoteFilters>) => {
    return setParams({ ...filters, page: '1' });
  };

  useEffect(() => {
    if (hasInteractedWithPaginator) {
      setQueryParams(params);
    }
  }, [params, setQueryParams, hasInteractedWithPaginator]);

  const {
    data: notesResponse,
    isPending: isLoadingNotes,
    isSuccess
  } = useAPI({
    queryKey: ['getNotes', object_id, params],
    queryFn: async () => {
      return await noteRepository.getNotes({
        params: { ...params, object_id: object_id, ordering: '-created_at' }
      });
    }
  });

  useEffect(() => {
    if (isSuccess) {
      if (notesResponse?.page) {
        setParams({ ...params, page: notesResponse.page.toString() });
      }

      if (notesResponse?.total_pages) {
        setTotalPages(notesResponse.total_pages);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, notesResponse]);

  const { currentPage, goToPage, nextPage, prevPage } = usePagination(
    parseInt(params.page, 10),
    totalPages
  );

  useEffect(() => {
    setParams((currentParams) => ({ ...currentParams, page: currentPage.toString() }));
  }, [currentPage, hasInteractedWithPaginator]);

  const handlePageChange = (page: number) => {
    setHasInteractedWithPaginator(true);
    goToPage(page);
  };

  const handleNextPage = () => {
    setHasInteractedWithPaginator(true);
    nextPage();
  };

  const handlePrevPage = () => {
    setHasInteractedWithPaginator(true);
    prevPage();
  };

  const newNoteParams = {
    objectId: object_id,
    returnTo: window.location.pathname,
    contentTypeName: contentTypeName
  };

  return (
    <Container type="page">
      <div className="flex flex-row items-center justify-between pb-2 bg-gray-100">
        <PageHeader headingText="Notes" />

        <div className="ml-3">
          <Button onClick={() => navigateToNewNote(newNoteParams)}>New Note</Button>
        </div>
      </div>

      <NoteFilterForm
        activeFilters={params}
        setFilters={setFilters}
        contentTypeName={contentTypeName}
      />

      <Pagination
        currentPage={currentPage}
        goToPage={handlePageChange}
        nextPage={handleNextPage}
        prevPage={handlePrevPage}
        totalPages={totalPages}>
        {isLoadingNotes && <LoadingSkeleton />}

        {!notesResponse?.results.length ? (
          <div className="text-center p-8">
            <Heading text={`No notes found for ${object_name}`} value={3} />
          </div>
        ) : (
          <ul role="list">
            {notesResponse?.results.map((note: Note) => (
              <li key={note.id}>
                <NoteCard note={note} />
              </li>
            ))}
          </ul>
        )}
      </Pagination>
    </Container>
  );
};

export default memo(NoteList);
